import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/web/frontend/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/web/frontend/markdoc/tags.js'
import * as nodes from '/vercel/path0/web/frontend/markdoc/nodes.js'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer({"allowComments":true});

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Setup Okta SSO\n---\n\n{% callout title=\"Okta SSO Eligibility\" type=\"note\" %}\n\nSELECT's Okta SSO integration is an add-on feature. Please contact SELECT to determine your eligiblity and receive a quote.\n\n{% /callout %}\n\n## Connecting Okta\nTo manage access and authentication to SELECT using your Okta account, please follow the steps below. \n\n### Step 1: Create App Integration\n\nLaunch the create app integration flow in the Okta console.\n\n{% blog-post-image imageName=\"okta1\" alt=\"SELECT Okta integration setup step #1\" /%}\n\nSelect OIDC for the sign-in method, and web application as the application type.\n\n{% blog-post-image imageName=\"okta2\" alt=\"SELECT Okta integration setup step #2\" /%}\n\nOn the next page, set the app name to SELECT. Check all the grant type boxes so that they match the screenshot below. Set the sign-in redirect URI to `https://auth.select.dev/login/callback`.\n\n{% blog-post-image imageName=\"ec724c42-a209-48e5-89b9-61e5b5984900\" alt=\"SELECT Okta integration setup step #3\" /%}\n\nOn the next page, scroll to ‘General Settings’ and click ‘Edit’. Under ‘Login’, set the ‘Login initiated by’ dropdown to ‘Either Okta or app’. Under ‘Application visibility’, check the box next to ‘Display application icon to users’. You can find your `ORGANIZATION_ID` value from the user pane on the bottom left corner of the app.\n\n{% blog-post-image imageName=\"f7fb11a4-c33c-4bb0-e8e8-2f4fb85ba100\" alt=\"Find your organization ID SELECT\" /%}\n\nSet the Initiate Login URI value to `https://select.dev/api/auth/login?organization=ORGANIZATION_ID`, replacing `ORGANIZATION_ID` with the value you have.\n\n{% blog-post-image imageName=\"17ebb209-96d2-4468-b1a3-d557a8e2e500\" alt=\"SELECT Okta integration setup step #4\" /%}\n\nSet Federation Broker Mode to Disabled to ensure that the app tile shows up for users. Add the required assignments under the ‘Assignments’ tab at the top of the page.\n\n{% blog-post-image imageName=\"okta5\" alt=\"SELECT Okta integration setup step #5\" /%}\n\n### Step 2: Send information to SELECT team\n\nOnce complete, let the SELECT team ([hello@select.dev](mailto:hello@select.dev)) know the following values:\n\n- Client ID and secret for the newly created application integration\n    \n{% blog-post-image imageName=\"okta6\" alt=\"SELECT Okta integration setup step #6\" /%}\n    \n- The Okta domain. This is usually the domain found by clicking your email address in the top right, as shown in the below screenshot. If you've configured the app to use a custom domain, however, please specify that instead.\n\n{% blog-post-image imageName=\"okta7\" alt=\"SELECT Okta integration setup step #7\"/%}\n\n\n## Mapping SSO Groups to Roles in SELECT\n\nThrough the User Roles & Permissions in SELECT, users can enable fine-grained access control in SELECT.\n\nRather than manually [assigning each user a role](/docs/manage-access#assigning-roles) in SELECT, you can configure roles to be automatically assigned based on the user's Okta SSO group.\n\nTo do this, the SELECT integration you set up in Okta needs to be configured to pass your employee's Okta groups to SELECT. Follow the steps below to add the necessary group claims.\n\n1. In Okta, click **Applications** in the sidebar\n2. Click the SELECT app\n\n{% blog-post-image imageName=\"sso-group-mappings-2-5f3cb51d7bcaedbe1b261625d064aad40ef92322a802907a0535b827e85b29ca\" alt=\"\"/%}\n\n3. Click the **Sign On** tab\n\n{% blog-post-image imageName=\"sso-group-mappings-4-a94c684018cc38af3d0c3fe51eebdbeb9314072a894b2f309fadd52554b119e1\" alt=\"\"/%}\n\n4. Scroll down to **OpenID Connect ID Token** section and click **Edit**.\n\n{% blog-post-image imageName=\"sso-group-mappings-0-d8ecb062c24e703a588e24f7a24e7d1ccb20363c27ad6cd9af433fb419d57952\" alt=\"\"/%}\n\n5. Set the **Group claims type** to `Filter`\n6. Set **Groups claim filter** to `groups` (which is pre-filled) and for pattern pick matches regex and enter `.*` if you want to send along all the groups. If you want to restrict the groups that are made available to select choose another option. You can learn more about the [Groups Claim here](https://developer.okta.com/docs/guides/customize-tokens-groups-claim/main/).\n7. Click Save\n\n{% blog-post-image imageName=\"sso-group-mappings-1-b7c9ccfbf4dc872e550b688a753197b22207af4ab3efb373d6da8a7a71cbf32c\" alt=\"\"/%}\n\n8. Log in / out of SELECT, and validate that the SSO groups are being exposed to SELECT by checking the user's roles\n\n{% blog-post-image imageName=\"sso-user-sidebar\" alt=\"SELECT User Sidebar Roles\" /%}\n\n9. Head back to our [Roles documentation](/docs/manage-access#integration-with-sso) and add the group mappings in SELECT.\n";
const filepath = "/docs/manage-access/okta-sso.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens, {slots: false});

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

async function getMarkdocData(context = {}) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  // Removes undefined
  return JSON.parse(
    JSON.stringify({
      content,
      frontmatter,
      file: {
        path: filepath,
      },
    })
  );
}

export async function getStaticProps(context) {
  return {
    props: {
      markdoc: await getMarkdocData(context),
    },
  };
}

export default function MarkdocComponent(props) {
  const markdoc = props.markdoc;
  // Only execute HMR code in development
  return renderers.react(markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
